/* eslint-disable react/no-unstable-nested-components */
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box, InputLabel, OutlinedInput } from '@mui/material';

import { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import LeadGenerationContext from '../../../context/LeadGenerationContext';
import { CustomDobStyles as classes } from './CustomDobStyles';
import 'dayjs/locale/es';

import { CommonStyles } from '../CommonStyles';
import { commonTernary, getLang, getRoot, isWebComponentFlow } from '../../commonCode';
import { REQUIRED } from '../../../constants';
import { useShadowRootContainer } from '../../../WebComponents/hooks/useShadowRootContainer';

export const CustomDob = ({
  label,
  containerClass,
  id,
  dataTestIdForLabel,
  required,
  value,
  format,
  onChange,
  handleChange,
  inputLabelClassName,
  dataTestId,
  autoFocus,
  error,
  errorBorder,
  InputClassName,
  placeholder,
  fullWidth,
  disabled,
  minDatePicker,
  maxDatePicker,
  msgSelectDate,
  inputFormat,
  shouldDisableDate,
  transactionDate,
  dueDate,
  noIcon,
  isFlowTypeETB,
  isBundle = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dobHostRef = useRef(null);
  const baseContext = isWebComponentFlow();
  const { language, theme, isLightTheme, wcFlow } = baseContext;
  const commonStyles = CommonStyles(theme);

  const popperContainer = useShadowRootContainer(wcFlow, dobHostRef);

  useEffect(() => {
    const calendarId = getRoot().querySelector('#calendarBtn > button');
    if (calendarId) {
      calendarId.setAttribute('aria-label', language === 'EN' ? 'Calendar' : 'Elige la fecha');
    }
  }, [language]);
  return (
    <>
      <InputLabel
        htmlFor={id}
        sx={{
          ...classes.label_title,
          color: theme?.palette?.mode === 'dark' ? theme?.palette?.primary?.main : undefined,
        }}
        data-testid={dataTestIdForLabel || null}
        required={required || false}
        aria-label={`${t('Date of Birth', { defaultValue: 'Date of Birth' })} ${t(REQUIRED, {
          defaultValue: REQUIRED,
        })}`}
      >
        {isFlowTypeETB ? (
          t('Date of birth', { defaultValue: 'Date of birth' })
        ) : (
          <span
            style={{
              color: errorBorder && isBundle ? 'red' : 'inherit',
            }}
          >
            {t('Date of Birth', { defaultValue: 'Date of Birth' })}
          </span>
        )}
      </InputLabel>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getLang(language, true)}>
        <div ref={dobHostRef}>
          {' '}
          {/*  div always forwards ref correctly unlike MUI components */}
          <DesktopDatePicker
            defaultCalendarMonth={commonTernary(dueDate, dueDate, null)}
            value={!value ? null : value}
            label="dob"
            open={open}
            views={['year', 'month', 'day']}
            openTo={value ? 'day' : 'year'}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            inputFormat={inputFormat}
            onChange={(dateValue) => {
              if (!disabled) {
                setOpen(true);
              }
              setTimeout(() => {
                getRoot().getElementById(id)?.focus();
              }, 100);
              onChange(dateValue?.$d ? dateValue.$d : dateValue);
            }}
            // disablePortal={!wcFlow} // Portal INTO shadow root for wcFlow; disabled for regular web
            slotProps={{
              day: {
                sx: {
                  '&.Mui-selected': {
                    backgroundColor: '#661C69',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#4a124c',
                    },
                  },
                },
              },
              month: {
                sx: {
                  '&.Mui-selected': {
                    backgroundColor: '#661C69',
                    color: '#fff',
                  },
                },
              },
              year: {
                sx: {
                  '&.Mui-selected': {
                    backgroundColor: '#661C69',
                    color: '#fff',
                  },
                },
              },
              popper: wcFlow
                ? {
                    container: popperContainer, // Now correctly resolves via div wrapper ref
                    strategy: 'fixed',
                    placement: 'bottom-start',
                    modifiers: [
                      {
                        name: 'flip',
                        options: { fallbackPlacements: ['top'] },
                      },
                      {
                        name: 'computeStyles',
                        options: {
                          adaptive: false,
                          gpuAcceleration: false,
                        },
                      },
                      {
                        name: 'offset',
                        options: { offset: [0, 4] },
                      },
                      {
                        name: 'preventOverflow',
                        options: { padding: 8 },
                      },
                    ],
                  }
                : {
                    modifiers: [
                      {
                        name: 'offset',
                        options: { offset: [0, 0] },
                      },
                    ],
                  },
            }}
            minDate={minDatePicker}
            maxDate={maxDatePicker}
            shouldDisableDate={shouldDisableDate}
            disabled={commonTernary(disabled, disabled, false)}
            components={{
              OpenPickerIcon: () => (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g id=" calendar">
                    <path
                      id="Icon"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7 16C7 15.45 7.45 15 8 15C8.55 15 9 15.45 9 16C9 16.55 8.55 17 8 17C7.45 17 7 16.55 7 16ZM12 15H16C16.55 15 17 15.45 17 16C17 16.55 16.55 17 16 17H12C11.45 17 11 16.55 11 16C11 15.45 11.45 15 12 15ZM18 20H6C5.449 20 5 19.551 5 19V13H19V19C19 19.551 18.551 20 18 20ZM6 6H7V7C7 7.55 7.45 8 8 8C8.55 8 9 7.55 9 7V6H15V7C15 7.55 15.45 8 16 8C16.55 8 17 7.55 17 7V6H18C18.551 6 19 6.449 19 7V11H5V7C5 6.449 5.449 6 6 6ZM18 4H17V3C17 2.45 16.55 2 16 2C15.45 2 15 2.45 15 3V4H9V3C9 2.45 8.55 2 8 2C7.45 2 7 2.45 7 3V4H6C4.346 4 3 5.346 3 7V19C3 20.654 4.346 22 6 22H18C19.654 22 21 20.654 21 19V7C21 5.346 19.654 4 18 4Z"
                      fill={disabled ? '#b28db4' : theme?.palette?.orientalPrimary?.main}
                    />
                  </g>
                </svg>
              ),
            }}
            InputAdornmentProps={{ className: classes.dobPick, id: 'calendarBtn' }}
            renderInput={({ inputRef, inputProps, InputProps }) => (
              <Box
                sx={
                  !isBundle
                    ? commonTernary(containerClass, containerClass, classes.dobContainer)
                    : undefined
                }
              >
                <OutlinedInput
                  ref={inputRef}
                  {...inputProps}
                  placeholder={placeholder}
                  onClick={(e) => {
                    if (!disabled) {
                      setOpen(true);
                    }
                    setTimeout(() => {
                      getRoot().getElementById(id).focus();
                    }, 100);
                  }}
                  id={id}
                  name={id}
                  sx={[
                    {
                      '& input': {
                        fontSize: '16px',
                      },
                      '& input::placeholder': {
                        fontSize: '16px',
                      },
                    },
                    ...(error
                      ? [classes.outlined_inputs_error, classes.select_inputs_err]
                      : disabled
                      ? [classes.dob_month_year_select_disabled]
                      : [classes.outlined_inputs]),
                    ...(isBundle
                      ? [
                          commonStyles.customBorderRadius,
                          commonStyles.outlined_input_bundle,
                          {
                            backgroundColor: errorBorder
                              ? '#D5151F1A'
                              : theme?.palette?.fieldsBackground?.main,
                            border: errorBorder
                              ? `2px solid ${theme?.palette?.danger?.main}`
                              : `1px solid ${theme?.palette?.fieldsBorder?.main}`,
                            '&.Mui-focused': {
                              border: `1px solid ${theme?.palette?.primary?.main}`,
                            },
                          },
                          disabled && {
                            border: commonTernary(
                              isBundle,
                              `1px solid ${theme?.palette?.fieldsBorder?.main}`,
                              '1px solid #E4E9F2'
                            ),
                            backgroundColor: commonTernary(
                              isBundle,
                              theme?.palette?.disabledAddress?.main,
                              '#F3F3F3'
                            ),
                            color: theme?.palette?.disabled?.main,
                            WebkitTextFillColor: theme?.palette?.disabled?.main,
                          },
                        ]
                      : [
                          {
                            '&.Mui-focused': {
                              border: `1px solid ${theme?.palette?.primary?.main}`,
                            },
                          },
                        ]),
                  ]}
                  fullWidth
                  inputProps={{
                    'aria-label': msgSelectDate,
                    'aria-required': `${required || false}`,
                    'data-testid': dataTestId,
                  }}
                  endAdornment={disabled ? '' : InputProps?.endAdornment}
                  slotProps={{
                    notchedOutline: {
                      sx: isBundle && commonStyles.noBorder,
                    },
                    input: {
                      sx: {
                        ...(disabled && {
                          WebkitTextFillColor: 'unset !important',
                        }),
                        '&:-webkit-autofill': {
                          WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                          WebkitTextFillColor:
                            isBundle && !isLightTheme ? '#ffffff !important' : 'inherit !important',
                          transition: 'background-color 5000s ease-in-out 0s',
                        },
                      },
                    },
                  }}
                />
              </Box>
            )}
          />
        </div>
      </LocalizationProvider>
      {error ? (
        <Box sx={commonTernary(disabled, classes.disableError, classes.error)} role="alert">
          {t(error, { defaultValue: error })}
        </Box>
      ) : (
        ''
      )}
    </>
  );
};
CustomDob.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string,
  containerClass: PropTypes.object,
  inputLabelClassName: PropTypes.object,
  InputClassName: PropTypes.object,
  required: PropTypes.bool,
  value: PropTypes.string,
  format: PropTypes.func,
  onChange: PropTypes.func,
  fullWidth: PropTypes.bool,
  handleChange: PropTypes.func,
  minDatePicker: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  maxDatePicker: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  msgSelectDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  autoFocus: PropTypes.bool,
  dataTestId: PropTypes.string,
  shouldDisableDate: PropTypes.bool,
  transactionDate: PropTypes.string,
  dueDate: PropTypes.string,
  noIcon: PropTypes.bool,
  placeholder: PropTypes.string,
  day: PropTypes.string,
  month: PropTypes.string,
  disabled: PropTypes.bool,
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  language: PropTypes.string,
  dob: PropTypes.string,
  dataTestIdForLabel: PropTypes.string,
  error: PropTypes.string,
  errorBorder: PropTypes.string,
  inputFormat: PropTypes.string,
  isFlowTypeETB: PropTypes.bool,
  isBundle: PropTypes.bool,
};
